import { useMemo } from 'react';
import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';
import subMinutes from 'date-fns/subMinutes';
import { useEditor, useNode } from '@splashthat/splash-cms-engine';
import { TYPE_VIRTUAL_EVENT_MULTISESSION } from 'common/constants/virtualEvent';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import {
    CONFIRMATION_HEADLINE,
    CONFIRMATION_MESSAGING,
    SESSIONS_PAGE,
    VIRTUAL_EVENT_PAGE,
    EVENT_DATE,
    START_TIME,
    DYNAMIC_TAGS,
} from 'common/components/TouchpointEditor/constants/dynamicTags';
import { appendGuestAccessHash } from 'common/components/TouchpointEditor/utils';

const convertTagToRegexForm = (tag) => tag.replace(/(\[|\])/g, '\\$1');

const VIRTUAL_EVENT_URL = '/virtual';
const AGENDA_PAGE_URL = '/agenda';
const LEGACY_SESSIONS_PAGE_URL = '?stageType=session';

export default function useDynamicTags(text = '') {
    const { event, guest } = useTouchpointData();
    const node = useNode();
    const { selected, hovered } = useEditor((state) => ({
        selected: state.events.selected === node.id,
        hovered: state.events.hovered === node.id,
    }));

    const getUrlWithGuestAccessHash = (url) => appendGuestAccessHash(url, guest.guestAccessHash);

    const isReplaceDisabled = selected || hovered;
    const isVirtualSessionEvent = event?.multisession === TYPE_VIRTUAL_EVENT_MULTISESSION;

    const confirmationHeadline = event?.confirmationsConfig?.headline || '';
    const confirmationMessaging = event?.confirmationsConfig?.messaging || '';
    const sessionsPageUrl = getUrlWithGuestAccessHash(
        isVirtualSessionEvent ? AGENDA_PAGE_URL : LEGACY_SESSIONS_PAGE_URL,
    );
    const virtualEventPageUrl = getUrlWithGuestAccessHash(VIRTUAL_EVENT_URL);
    const checkinDate = event.startTime ? subMinutes(parseISO(event.startTime), 30) : null;
    const date = checkinDate ? format(checkinDate, 'cccc, MMMM do') : '';
    const time = checkinDate ? format(checkinDate, ' h:mm a') : '';

    const replacementSources = {
        [CONFIRMATION_HEADLINE]: confirmationHeadline,
        [CONFIRMATION_MESSAGING]: confirmationMessaging,
        [SESSIONS_PAGE]: sessionsPageUrl,
        [VIRTUAL_EVENT_PAGE]: virtualEventPageUrl,
        [EVENT_DATE]: date || 'soon',
        [START_TIME]: time || '',
    };

    const replacedContent = useMemo(
        () =>
            DYNAMIC_TAGS.reduce(
                (content, tag) =>
                    content.replace(new RegExp(convertTagToRegexForm(tag), 'gi'), replacementSources[tag]),
                text,
            ),
        [confirmationHeadline, confirmationMessaging, date, time, text, sessionsPageUrl, virtualEventPageUrl],
    );

    return isReplaceDisabled ? text : replacedContent;
}
